/**
 * What the user watches while a filing is being imported.
 *
 * The wait is minutes, not seconds, and it got longer on purpose: a scanned
 * filing is OCR'd page by page, and an import that does not balance is now read
 * again up to three times rather than handing over a model whose figures do not
 * add up. Against that, a single line of text in a box is not enough - it gives
 * no sense of progress, no sense of how long is left, and no reason to believe
 * anything is still happening.
 *
 * So this shows three things a spinner cannot:
 *
 *   - WHERE IT IS. The import already reports each stage; those messages were
 *     being rendered as one anonymous line. They map onto four stages, and seeing
 *     three of them ticked off is the difference between waiting and being stuck.
 *   - HOW LONG IT HAS TAKEN. For a multi-minute job a running timer is the single
 *     most reassuring element on the screen, and it costs one interval.
 *   - WHAT IT IS ACTUALLY DOING. The detail lines rotate through real checks the
 *     importer performs, not filler: they are the arithmetic that decides whether
 *     a reading is accepted.
 *
 * Deliberately NOT an animated GIF. A GIF is a few hundred kilobytes that cannot
 * be recoloured, goes soft on a high-density screen, and says the same thing
 * whatever stage the import is at. The animation here is SVG and CSS - a few
 * hundred bytes, sharp at any size, and it changes with the stage, which makes it
 * information rather than decoration. If illustrations are wanted later they drop
 * into `Illustration` without touching anything else.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

/** The stages the backend reports, in the order they happen. */
type Stage = 'reading' | 'figures' | 'model' | 'checking';

const STAGES: { id: Stage; label: string }[] = [
  { id: 'reading', label: 'Reading the pages' },
  { id: 'figures', label: 'Reading the figures' },
  { id: 'model', label: 'Building the model' },
  { id: 'checking', label: 'Checking it adds up' },
];

/**
 * Which stage a progress message belongs to.
 *
 * Matched on the wording the extractor actually emits, listed here so the two can
 * be compared side by side rather than guessed at:
 *
 *   "Reading the PDF…"
 *   "This PDF is scanned, so it was read by OCR first…"
 *   "This PDF has no embedded text (81 scanned pages), so it is being read…"
 *   "This filing is 119 pages, so the accounts section is being located first."
 *   "Reading pages 23-60 of 60, which hold the accounts."
 *   "Extracting the figures…"
 *   "Mapping the figures onto the model…"
 *   "The figures did not add up; asking for a correction (1 of 2)…"
 *   "Reading the balance sheet on its own…"
 *   "The balance sheet did not balance, so the filing is being read again…"
 *
 * An unrecognised message leaves the stage where it was rather than resetting to
 * the first: a new message in the extractor should not make the panel appear to
 * start over.
 */
export function stageFor(message: string, previous: Stage = 'reading'): Stage {
  const text = message.toLowerCase();
  if (text.includes('did not add up') || text.includes('did not balance')) {
    return 'checking';
  }
  if (text.includes('balance sheet on its own')) return 'checking';
  if (text.includes('mapping the figures')) return 'model';
  if (text.includes('extracting the figures')) return 'figures';
  if (
    text.includes('reading the pdf') ||
    text.includes('scanned') ||
    text.includes('embedded text') ||
    text.includes('accounts section') ||
    text.includes('reading pages')
  ) {
    return 'reading';
  }
  return previous;
}

/**
 * True where the message says the import is having another go.
 *
 * Worth surfacing rather than hiding. A user told the filing is being read a
 * second time understands why the wait grew; one who is not just sees it hang.
 */
export function isRetrying(message: string): boolean {
  const text = message.toLowerCase();
  return text.includes('read again') || text.includes('asking for a correction');
}

/** Real checks the importer runs, for the line under the stages. */
const WHAT_IT_CHECKS = [
  'Assets less liabilities must equal equity — in this model cash comes from the cash flow, so nothing absorbs a mistake quietly.',
  'Every cost line is added up and compared with the operating profit the filing prints.',
  'Closing cash is checked against the cash flow statement, and against the balance sheet.',
  'The fixed asset note is rolled forward class by class and must arrive at net fixed assets.',
  'The statement of changes in equity must close on the equity the balance sheet reports.',
  'Trade debtors, creditors, corporation tax and VAT are checked against the balances the filing supplies.',
  'Where a figure the accounts print disagrees with the model, the filing is read again rather than the difference being hidden.',
];

function useElapsed(): number {
  const started = useRef(Date.now());
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const timer = setInterval(
      () => setSeconds(Math.floor((Date.now() - started.current) / 1000)),
      1000,
    );
    return () => clearInterval(timer);
  }, []);
  return seconds;
}

export function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${String(seconds % 60).padStart(2, '0')}s`;
}

/**
 * A page with a scanning line travelling down it, and figures darkening as it
 * passes. SVG and CSS, so it stays sharp and weighs nothing.
 *
 * `busy` goes false once the reading is done, which stops the scan and leaves the
 * figures in place - the picture then matches what is happening rather than
 * animating for its own sake. Motion is dropped entirely for anyone who has asked
 * for less of it.
 */
function Illustration({ busy }: { busy: boolean }) {
  return (
    <svg
      viewBox="0 0 64 80"
      className="h-20 w-16 shrink-0"
      role="img"
      aria-label="A page being read"
    >
      <style>{`
        @keyframes v2-scan { from { transform: translateY(4px) } to { transform: translateY(68px) } }
        @keyframes v2-ink { 0%, 40% { opacity: .2 } 60%, 100% { opacity: 1 } }
        .v2-scanline { animation: v2-scan 2.4s cubic-bezier(.4,0,.6,1) infinite alternate }
        .v2-row { animation: v2-ink 2.4s ease-in-out infinite }
        @media (prefers-reduced-motion: reduce) {
          .v2-scanline, .v2-row { animation: none }
        }
      `}</style>

      <rect x="6" y="2" width="52" height="76" rx="3" fill="#fff" stroke="#bfdbfe" />

      {[14, 22, 30, 38, 46, 54, 62].map((y, index) => (
        <g
          key={y}
          className={busy ? 'v2-row' : undefined}
          style={{ animationDelay: `${index * 0.18}s` }}
        >
          <rect x="12" y={y} width="22" height="3" rx="1.5" fill="#93c5fd" />
          <rect x="40" y={y} width="12" height="3" rx="1.5" fill="#3b82f6" />
        </g>
      ))}

      {busy && (
        <g className="v2-scanline">
          <rect x="6" y="0" width="52" height="2" fill="#2563eb" opacity="0.85" />
          <rect x="6" y="2" width="52" height="6" fill="url(#v2-fade)" />
        </g>
      )}
      <defs>
        <linearGradient id="v2-fade" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#2563eb" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export default function PdfImportProgress({ message }: { message: string }) {
  const elapsed = useElapsed();
  const [stage, setStage] = useState<Stage>('reading');

  // Carried forward rather than recomputed from scratch, so an unrecognised
  // message cannot make the panel appear to start again.
  useEffect(() => setStage((previous) => stageFor(message, previous)), [message]);

  const retrying = isRetrying(message);
  const reached = STAGES.findIndex((entry) => entry.id === stage);

  // One line at a time, changed slowly enough to be read. Driven by the elapsed
  // seconds rather than its own timer, so the component has one clock.
  const detail = useMemo(
    () => WHAT_IT_CHECKS[Math.floor(elapsed / 9) % WHAT_IT_CHECKS.length],
    [elapsed],
  );

  return (
    <div className="mt-4 rounded border border-blue-200 bg-blue-50 px-4 py-4 text-sm text-blue-900">
      <div className="flex gap-4">
        <Illustration busy={stage === 'reading' || retrying} />

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-medium">Importing the filing…</span>
            <span
              className="shrink-0 font-mono text-xs text-blue-700"
              aria-label="Time taken so far"
            >
              {formatElapsed(elapsed)}
            </span>
          </div>

          <ol className="mt-2 space-y-1">
            {STAGES.map((entry, index) => {
              const done = index < reached;
              const current = index === reached;
              return (
                <li
                  key={entry.id}
                  className={[
                    'flex items-center gap-2 text-xs',
                    done
                      ? 'text-blue-700'
                      : current
                        ? 'font-medium text-blue-900'
                        : 'text-blue-400',
                  ].join(' ')}
                >
                  <span aria-hidden className="w-3 text-center">
                    {done ? '✓' : current ? '›' : '·'}
                  </span>
                  <span>{entry.label}</span>
                  {current && (
                    <span className="h-1 w-1 animate-pulse rounded-full bg-blue-600" />
                  )}
                </li>
              );
            })}
          </ol>

          <p className="mt-2 text-blue-800">{message}</p>

          {retrying ? (
            <p className="mt-1 text-xs text-blue-700">
              The first reading did not add up, so it is being read again. This is
              normal, and the figures come out better for it — nothing is wrong.
            </p>
          ) : (
            <p className="mt-1 text-xs text-blue-700">
              A scanned filing is read page by page and can take several minutes.
            </p>
          )}

          <p className="mt-2 border-t border-blue-200 pt-2 text-xs text-blue-700">
            {detail}
          </p>
        </div>
      </div>
    </div>
  );
}
